import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { STRAPI_URL } from '../services/api';

const routeMap = {
    'Hizmetler': '/hizmetler',
    'Personel': '/personel',
    'Tesisler': '/tesisler',
    'Belgeler': '/belgeler',
    'Ağ Cihazları': '/ag-cihazlari',
    'Ag Cihazlari': '/ag-cihazlari',
    'Donanım': '/donanim',
    'Donanim': '/donanim',
    'Fiziksel Medya': '/fiziksel-medya',
    'Veri': '/veri',
    'Yazılım': '/yazilim',
    'Yazilim': '/yazilim',
    'Medya': '/fiziksel-medya',
};

const i18nKeyMap = {
    'Yazılım': 'Yazilim',
    'Ağ Cihazları': 'AgCihazlari',
    'Fiziksel Medya': 'FizikselMedya',
    'Donanım': 'Donanim',
    'Veri': 'Veri',
    'Personel': 'Personel',
    'Belgeler': 'Belgeler',
    'Tesisler': 'Tesisler',
    'Hizmetler': 'Hizmetler',
    'Medya': 'FizikselMedya',
};

const DEFAULT_SOLUTIONS = [
    {
        id: 'f1',
        baslik: 'KONTROL RİSK MATRİSİ',
        ikon_char: '💻',
        image: [{ url: '/images/FeatureShowcase/kontrolMatrisi.png' }]
    },
    {
        id: 'f2',
        baslik: 'GÖSTERGE PANELİ',
        ikon_char: '🖥️',
        image: [{ url: '/images/FeatureShowcase/gostergePaneli.png' }]
    },
    {
        id: 'f3',
        baslik: 'ENVANTER YÖNETİMİ',
        ikon_char: '🌐',
        image: [{ url: '/images/FeatureShowcase/envanterYonetimi.png' }]
    },
    {
        id: 'f4',
        baslik: 'SİSTEM ROLLER/İZİNLER',
        ikon_char: '🗄️',
        image: [{ url: '/images/FeatureShowcase/sistemRoller.png' }]
    },
    {
        id: 'f5',
        baslik: 'DEPARTMAN YÖNETİMİ',
        ikon_char: '👥',
        image: [{ url: '/images/FeatureShowcase/departmanYonetimi.png' }]
    },
    {
        id: 'f6',
        baslik: 'PERFORMANS',
        ikon_char: '📄',
        image: [{ url: '/images/FeatureShowcase/performans.png' }]
    },
    {
        id: 'f7',
        baslik: 'RİSK',
        ikon_char: '🏢',
        image: [{ url: '/images/FeatureShowcase/risk.png' }]
    },
    {
        id: 'f8',
        baslik: 'AKTİVİTE GÜNLÜĞÜ',
        ikon_char: '☁️',
        image: [{ url: '/images/FeatureShowcase/aktiviteGunlugu.png' }]
    },
    {
        id: 'f9',
        baslik: 'POLİTİKALAR',
        ikon_char: '💾',
        image: [{ url: '/images/FeatureShowcase/politikalar.png' }]
    },
];

const FeatureShowcase = ({ data }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const items = useMemo(() => {
        if (data?.items?.length > 0) return data.items;
        if (data?.features?.length > 0) return data.features;
        if (data?.cards?.length > 0) return data.cards;
        if (data?.modules?.length > 0) return data.modules;
        return DEFAULT_SOLUTIONS;
    }, [data]);

    const [activeTab, setActiveTab] = useState(items[0] || null);

    useEffect(() => {
        if (items.length > 0) {
            setActiveTab(items[0]);
        }
    }, [items]);

    const getTitle = (item) => {
        const isEn = i18n.language === 'en';
        const val = isEn
            ? (item?.label_en || item?.label || item?.baslik || item?.Title || item?.title || '')
            : (item?.label || item?.baslik || item?.Title || item?.title || '');
        return typeof val === 'string' ? val : '';
    };

    const resolveMediaObject = (item, fieldName) => {
        const value = item?.[fieldName];
        if (Array.isArray(value) && value.length > 0) return value[0];
        if (value?.url) return value;
        return null;
    };

    const resolveMediaUrl = (item, fieldName) => {
        const media = resolveMediaObject(item, fieldName);

        if (media?.url) {
            if (media.url.startsWith('http')) return media.url;

            if (media.url.startsWith('/images/')) return media.url;

            return `${STRAPI_URL}${media.url}`;
        }

        return null;
    };

    const getPreviewImageUrl = (item) => {
        return (
            resolveMediaUrl(item, 'previewImage') ||
            resolveMediaUrl(item, 'image') ||
            resolveMediaUrl(item, 'gorsel') ||
            resolveMediaUrl(item, 'ikon') ||
            item?.ikon_url ||
            item?.image?.[0]?.url ||
            null
        );
    };

    const getCardIcon = (item) => {
        return {
            iconName: null,
            iconImage:
                resolveMediaUrl(item, 'icon') ||
                resolveMediaUrl(item, 'ikon') ||
                resolveMediaUrl(item, 'image') ||
                resolveMediaUrl(item, 'gorsel') ||
                item?.ikon_url ||
                null,
        };
    };

    const getTranslatedLabel = (item) => {
        const raw = getTitle(item).trim();
        if (typeof raw !== 'string') return '';
        const i18nKey = i18nKeyMap[raw];
        return i18nKey ? t(`solutions.${i18nKey}`) : raw;
    };

    const handleScrollToCozumler = () => {
        const el = document.getElementById('cozumler');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const currentTab = activeTab || items[0] || null;
    const currentImage = getPreviewImageUrl(currentTab);

    return (
        <section className="bg-slate-50 px-4 md:px-8 py-12 flex items-center justify-center font-sans">
            <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

                <div className="lg:col-span-8 bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
                    <div className="bg-slate-900 p-2 flex gap-1.5 items-center px-4">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <div className="ml-4 h-4 w-48 bg-slate-700 rounded-full" />
                    </div>

                        <div className="relative bg-slate-100 w-full" style={{ aspectRatio: '1600/840' }}>
                        <AnimatePresence mode="wait">
                            {currentImage ? (
                                <motion.img
                                    key={currentTab?.id || 'default'}
                                    src={currentImage}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.25 }}
                                    className="w-full h-full object-contain"
                                    alt={getTitle(currentTab) || 'Görsel'}
                                />
                            ) : (
                                <motion.div
                                    key="placeholder"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="w-full h-full flex items-center justify-center bg-[#e8f5f2]"
                                >
                                    {currentTab?.ikon_char ? (
                                        <span className="text-6xl">
                                            {currentTab.ikon_char}
                                        </span>
                                    ) : (
                                        <span className="text-6xl">🖼️</span>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                <div className="lg:col-span-4 bg-white rounded-3xl p-8 shadow-xl border border-slate-100">
                    <h2 className="text-2xl font-bold mb-8 text-slate-900">
                        {(i18n.language === 'en' ? data?.title_en : data?.title)
                            || data?.title
                            || t('nav.solutions')}
                    </h2>

                    <div className="grid grid-cols-3 gap-4 mb-8">
                        {items.map((item, index) => {
                            const isActive = currentTab?.id === item.id;
                            const label = getTranslatedLabel(item);
                            const { iconName, iconImage } = getCardIcon(item);

                            return (
                                <button
                                    key={item.id || index}
                                    onClick={() => setActiveTab(item)}
                                    className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-200 aspect-square text-center min-h-[100px] border-none cursor-pointer ${isActive
                                        ? 'bg-emerald-100 text-emerald-900 ring-2 ring-emerald-500 shadow-sm'
                                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:scale-105'
                                        }`}
                                >
                                    <span className="text-[10px] font-bold leading-tight uppercase tracking-wide">
                                        {label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex items-center justify-between mt-6 gap-4">
                        <button
                            onClick={handleScrollToCozumler}
                            className="bg-transparent border-none text-slate-900 font-bold flex items-center gap-2 hover:opacity-80 cursor-pointer"
                        >
                            {t('nav.solutions')} →
                        </button>

                        <button
                            onClick={() => navigate('/iletisim')}
                            className="bg-emerald-400 hover:bg-emerald-500 text-slate-900 font-bold py-3 px-6 rounded-full transition-all shadow-lg shadow-emerald-200 border-none cursor-pointer"
                        >
                            {t('nav.demo')} →
                        </button>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default FeatureShowcase;